import * as React from "react";
import { DatePicker, Dropdown, IDropdownOption, MessageBar, MessageBarType, Spinner } from "office-ui-fabric-react";
import { getRoomBookings, getRoomInfo, loadRoomsBySite } from "../../../api/rooms";
import { IBooking } from "../../../types/Booking.types";
import { IRoom, RoomType } from "../../../types/SiteLocation.types";
import { DefaultButton } from "@fluentui/react";

export interface ISiteDayCalendarProps {
	siteId: number;
	roomType: RoomType;
}

interface ISiteRoomDayData {
	room: IRoom;
	roomInfo?: IRoom;
	bookings: IBooking[];
}

interface IRenderedBlock {
	id: string;
	title?: string;
	left: number;
	width: number;
	tooltip: string;
}

const MINUTES_IN_DAY = 24 * 60;
const LEFT_LABEL_WIDTH = 220;
const PIXELS_PER_HOUR = 90;
const PIXELS_PER_MINUTE = PIXELS_PER_HOUR / 60;
const LANE_HEIGHT = 44;
const LANE_INNER_TOP = 6;
const BLOCK_HEIGHT = 32;
const WORKING_DAY_START_MINUTES = 8 * 60;
const WORKING_DAY_END_MINUTES = 18 * 60;

type TimeRangeMode = "workingHours" | "allDay";

const getDayNumber = (date: Date) => {
	const day = date.getDay();
	return day === 0 ? 7 : day;
};

const toDateOnly = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const parseTimeToMinutes = (value: string) => {
	const [hoursStr, minutesStr] = value.split(":");
	const hours = Number(hoursStr);
	const minutes = Number(minutesStr);
	if (Number.isNaN(hours) || Number.isNaN(minutes)) {
		return 0;
	}
	return (hours * 60) + minutes;
};

const clampToDay = (minutes: number) => Math.max(0, Math.min(MINUTES_IN_DAY, minutes));

const getVisibleMinutesRange = (mode: TimeRangeMode) => {
	if (mode === "workingHours") {
		return {
		start: WORKING_DAY_START_MINUTES,
		end: WORKING_DAY_END_MINUTES,
	};
}

return {
	start: 0,
	end: MINUTES_IN_DAY,
	};
};

export const SiteDayCalendar = (props: ISiteDayCalendarProps) => {
	const [loading, setLoading] = React.useState<boolean>(true);
	const [error, setError] = React.useState<string>();
	const [siteRoomDayData, setSiteRoomDayData] = React.useState<ISiteRoomDayData[]>([]);
	const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
	const [timeRangeMode, setTimeRangeMode] = React.useState<TimeRangeMode>("workingHours");

	const changeSelectedDate = React.useCallback((daysToAdd: number) => {
		setLoading(true);
		setSiteRoomDayData((currentRoomData) => currentRoomData.map((roomData) => ({
			...roomData,
			bookings: [],
		})));
		setSelectedDate((currentDate) => {
			const nextDate = new Date(currentDate);
			nextDate.setDate(nextDate.getDate() + daysToAdd);
			return nextDate;
		});
	}, []);

	const setCalendarDate = React.useCallback((newDate: Date | null | undefined) => {
		if (!newDate) {
			return;
		}

		const normalizedDate = toDateOnly(newDate);
		setLoading(true);
		setSiteRoomDayData((currentRoomData) => currentRoomData.map((roomData) => ({
			...roomData,
			bookings: [],
		})));
		setSelectedDate(normalizedDate);
	}, []);

	const loadTodaySiteBookings = React.useCallback(async (abortController: AbortController) => {
		if (!props.siteId) {
		setLoading(false);
		return;
	}

	setLoading(true);
	setError(undefined);

	const startOfDay = toDateOnly(selectedDate);
	const endOfDay = new Date(startOfDay);
	endOfDay.setHours(23, 59, 59, 999);

	try {
		const roomsResponse = await loadRoomsBySite(props.roomType, props.siteId, abortController);
		if (!roomsResponse.ok) {
		if (abortController.signal.aborted) return;
		setError("Unable to load rooms for this site");
		setSiteRoomDayData([]);
		return;
	}

	const rooms = await roomsResponse.json() as IRoom[];

	const roomData = await Promise.all(
		rooms.map(async (room) => {
			const [roomInfoResponse, bookingsResponse] = await Promise.all([
			getRoomInfo(room.Id),
			getRoomBookings(
			{
				roomId: room.Id,
				fromDate: startOfDay,
				toDate: endOfDay,
			},
			abortController
		),
	]);

	const roomInfo = roomInfoResponse.ok
		? (await roomInfoResponse.json() as IRoom)
		: undefined;
		const bookings = bookingsResponse.ok
		? (await bookingsResponse.json() as IBooking[])
		: [];

	return {
		room,
		roomInfo,
		bookings,
		} as ISiteRoomDayData;
		})
	);

	if (abortController.signal.aborted) return;
		setSiteRoomDayData(roomData);
		} catch {
			if (abortController.signal.aborted) return;
			setError("Unable to load site day booking data");
			setSiteRoomDayData([]);
		} finally {
			if (!abortController.signal.aborted) {
			setLoading(false);
		}
	}
	}, [props.roomType, props.siteId, selectedDate]);

	React.useEffect(() => {
	const abortController = new AbortController();
	loadTodaySiteBookings(abortController);

	return () => {
		abortController.abort();
	};
	}, [loadTodaySiteBookings]);

	const selectedDateOnly = React.useMemo(() => toDateOnly(selectedDate), [selectedDate]);
	const selectedDayNumber = React.useMemo(() => getDayNumber(selectedDate), [selectedDate]);
	const selectedDayEnd = React.useMemo(() => {
		const endOfSelectedDay = new Date(selectedDateOnly);
		endOfSelectedDay.setDate(endOfSelectedDay.getDate() + 1);
		return endOfSelectedDay;
	}, [selectedDateOnly]);
	const visibleMinutes = React.useMemo(() => getVisibleMinutesRange(timeRangeMode), [timeRangeMode]);
	const timelineWidth = React.useMemo(
	() => (visibleMinutes.end - visibleMinutes.start) * PIXELS_PER_MINUTE,
		[visibleMinutes.end, visibleMinutes.start]
	);

	const hourMarkers = React.useMemo(() => {
	const markers: { key: number; label: string }[] = [];
	const startHour = Math.floor(visibleMinutes.start / 60);
	const endHour = Math.ceil(visibleMinutes.end / 60);

	for (let hour = startHour; hour < endHour; hour++) {
		markers.push({
		key: hour,
		label: `${hour.toString().padStart(2, "0")}:00`,
	});
	}

	return markers;
	}, [visibleMinutes.end, visibleMinutes.start]);

	const timeRangeOptions = React.useMemo(() => ([
	{ key: "workingHours", text: "Working hours (08:00 - 18:00)" },
	{ key: "allDay", text: "All day (00:00 - 24:00)" },
	] as IDropdownOption[]), []);

	const roomRows = React.useMemo(() => {
	return [...siteRoomDayData]
	.sort((a, b) => a.room.Name.localeCompare(b.room.Name))
	.map((roomData) => {
		const bookingBlocks = roomData.bookings
	.map((booking) => {
		const startDate = new Date(booking.Start);
		const endDate = new Date(booking.Finish);
		if (endDate <= selectedDateOnly || startDate >= selectedDayEnd) {
			return undefined;
		}

		const clampedStartDate = startDate < selectedDateOnly ? selectedDateOnly : startDate;
		const clampedEndDate = endDate > selectedDayEnd ? selectedDayEnd : endDate;
		const startMinutes = clampToDay((startDate.getHours() * 60) + startDate.getMinutes());
		const endMinutes = clampToDay((endDate.getHours() * 60) + endDate.getMinutes());
		const clampedStartMinutes = clampToDay((clampedStartDate.getHours() * 60) + clampedStartDate.getMinutes());
		const clampedEndMinutes = clampToDay((clampedEndDate.getHours() * 60) + clampedEndDate.getMinutes());
		if (clampedEndMinutes <= clampedStartMinutes) {
		return undefined;
	}

	const visibleStart = Math.max(clampedStartMinutes, visibleMinutes.start);
	const visibleEnd = Math.min(clampedEndMinutes, visibleMinutes.end);
	if (visibleEnd <= visibleStart) {
		return undefined;
	}

	const left = (visibleStart - visibleMinutes.start) * PIXELS_PER_MINUTE;
	const width = (visibleEnd - visibleStart) * PIXELS_PER_MINUTE;
	const title = booking.Description && booking.Description.trim() !== ""
		? booking.Description
		: (booking.OnBehalfOfEmail || booking.BookedByUserName || "Booking");

	return {
		id: booking.Id.toString(),
		title,
		left,
		width,
		tooltip: `${title} (${startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${endDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})`,
		} as IRenderedBlock;
	})
	.filter((b): b is IRenderedBlock => b !== undefined);

	const unavailableBlocks = (roomData.roomInfo?.RoomAvailability || [])
		.filter((slot) => {
		if (slot.Day !== selectedDayNumber) {
		return false;
	}

	const effectiveFrom = toDateOnly(new Date(slot.EffectiveFrom));
		if (selectedDateOnly < effectiveFrom) {
		return false;
	}

	if (slot.EffectiveTo) {
		const effectiveTo = toDateOnly(new Date(slot.EffectiveTo));
		if (selectedDateOnly > effectiveTo) {
			return false;
		}
	}

	return true;
	})
	.map((slot, index) => {
	const startMinutes = clampToDay(parseTimeToMinutes(slot.TimeStart));
	const endMinutes = clampToDay(parseTimeToMinutes(slot.TimeEnd));
	if (endMinutes <= startMinutes) {
		return undefined;
	}

	const visibleStart = Math.max(startMinutes, visibleMinutes.start);
	const visibleEnd = Math.min(endMinutes, visibleMinutes.end);
	if (visibleEnd <= visibleStart) {
		return undefined;
	}

	return {
		id: `${roomData.room.Id}-${index}`,
		left: (visibleStart - visibleMinutes.start) * PIXELS_PER_MINUTE,
		width: (visibleEnd - visibleStart) * PIXELS_PER_MINUTE,
		tooltip: `Unavailable (${slot.TimeStart} - ${slot.TimeEnd})`,
	} as IRenderedBlock;
	})
	.filter((slot): slot is IRenderedBlock => slot !== undefined);

	return {
		roomName: roomData.room.Name,
		bookingBlocks,
		unavailableBlocks,
	};
	});
	}, [selectedDateOnly, selectedDayNumber, selectedDayEnd, siteRoomDayData, visibleMinutes.end, visibleMinutes.start]);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 20 }}>
				<div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
					<Dropdown
						label="Time range"
						selectedKey={timeRangeMode}
						options={timeRangeOptions}
						onChange={(_, option) => {
						if (!option) return;
						setTimeRangeMode(option.key as TimeRangeMode);
						}}
						styles={{ root: { maxWidth: 320 } }}
					/>
					<DatePicker
						label="Date"
						value={selectedDate}
						onSelectDate={(date) => { setCalendarDate(date); }}
						allowTextInput={true}
						styles={{ root: { minWidth: 180 } }}
					/>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					<DefaultButton
						onClick={() => { changeSelectedDate(-1); }}
					>
						Previous Day
					</DefaultButton>
					<DefaultButton
						onClick={() => { changeSelectedDate(1); }}
					>
						Next Day
					</DefaultButton>
				</div>
			</div>

			<div style={{ fontWeight: 600, textAlign: "center", fontSize: 16 }}>
				{selectedDate.toLocaleDateString()}
			</div>

			{error && (
				<MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
			)}

			{loading && (
				<Spinner label="Loading site day bookings" />
			)}

			{!loading && !error && roomRows.length === 0 && (
				<MessageBar messageBarType={MessageBarType.info}>
					No rooms were found for this site.
				</MessageBar>
			)}

			{!loading && !error && roomRows.length > 0 && (
				<div style={{ overflowX: "auto", border: "1px solid #d0d0d0", borderRadius: 4 }}>
					<div style={{ minWidth: LEFT_LABEL_WIDTH + timelineWidth }}>
					<div style={{ display: "flex", borderBottom: "1px solid #d0d0d0", background: "#fafafa" }}>
					<div
						style={{
						width: LEFT_LABEL_WIDTH,
						padding: "10px 12px",
						fontWeight: 600,
						borderRight: "1px solid #d0d0d0",
						}}
					>
						Room
					</div>
					<div style={{ display: "flex", width: timelineWidth }}>
						{hourMarkers.map((hour) => (
							<div
								key={hour.key}
								style={{
								width: PIXELS_PER_HOUR,
								padding: "10px 6px",
								fontSize: 12,
								fontWeight: 600,
								borderRight: "1px solid #e7e7e7",
								boxSizing: "border-box",
							}}
						>
							{hour.label}
					</div>
			))}
		</div>
		</div>

		{roomRows.map((row) => (
			<div key={row.roomName} style={{ display: "flex", borderBottom: "1px solid #ececec" }}>
				<div
					style={{
						width: LEFT_LABEL_WIDTH,
						padding: "10px 12px",
						borderRight: "1px solid #ececec",
						fontWeight: 500,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						lineHeight: `${LANE_HEIGHT}px`,
					}}
					title={row.roomName}
					>
					{row.roomName}
				</div>
			<div
				style={{
					position: "relative",
					width: timelineWidth,
					height: LANE_HEIGHT,
					background: "linear-gradient(to right, #f8f8f8 1px, transparent 1px)",
					backgroundSize: `${PIXELS_PER_HOUR}px 100%`,
				}}
			>
				{row.unavailableBlocks.map((block) => (
				<div
					key={block.id}
					style={{
						position: "absolute",
						left: block.left,
						top: LANE_INNER_TOP,
						height: BLOCK_HEIGHT,
						width: block.width,
						borderRadius: 4,
						color: "#ffffff",
						// background: "repeating-linear-gradient(135deg, #e4e4e4, #e4e4e4 6px, #f0f0f0 6px, #f0f0f0 12px)",
						backgroundColor: "#3174ad",
						border: "1px solid #265985",
						boxSizing: "border-box",
						padding: "6px 8px",
						fontSize: 12,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis"					
					}}
					title={block.tooltip}
				>
					Unavailable
				</div>
				))}

		{row.bookingBlocks.map((block) => (
			<div
				key={block.id}
				style={{
					position: "absolute",
					left: block.left,
					top: LANE_INNER_TOP,
					height: BLOCK_HEIGHT,
					width: block.width,
					borderRadius: 4,
					backgroundColor: "#3174ad",
					border: "1px solid #265985",
					color: "#ffffff",
					padding: "6px 8px",
					fontSize: 12,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					boxSizing: "border-box",
				}}
				title={block.tooltip}
			>
				{block.title}
			</div>
		))}
		</div>
		</div>
		))}
		</div>
		</div>
		)}
		</div>
	);
};

export default SiteDayCalendar;
